/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { ToggleModalProps } from '../../../../components/modal/types';
import type { NewClassGet, StaffTeacher } from '@whizz/api-model';
import type { SyntheticEvent } from 'react';
import React, { useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { useModal } from '../../../../hooks/useModal';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../util/vars';
import { repository } from '@whizz/api-client';
import RosterManagementLayout from '../../../../layouts/RosterManagementLayout';
import { useSuccessErrorModal } from '../../../../hooks/useSuccessErrorModal';
import { staffEmptyData } from '../Staff/Staff';
import { Button, ChoiceGroup, Loader, Table } from '../../../../components';
import { createGlobalStyle } from 'styled-components';
import { CancelButtonModal } from '../../../../global';
import { ModalFooter } from '../../../../components/modal/Modal';
import { useIdentity } from '@whizz/react-identity';
import NoData from '../../../../global/sections/NoData';

const Tidy = (): JSX.Element => {

  const { me } = useIdentity();
  const [ successErrorModal, toggleMessageType ] = useSuccessErrorModal();

  const onSuccess = () => toggleMessageType('success');
  const onError = () => toggleMessageType('error');

  const staffQuery = useQuery(
    [ QUERIES_IDS.STAFF, 'tidyUp' ],
    () => repository.StaffRepository.getStaff(),
    {
      enabled: true,
      refetchOnMount: 'always',
      placeholderData: staffEmptyData,
    },
  );

  const classesQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES, 'tidyUp' ],
    () => repository.NewClassRepository.getClasses({
      includeArchived: false,
      includeStudentCount: true,
    }),
    {
      enabled: true,
      refetchOnMount: 'always',
      placeholderData: [] as NewClassGet,
    },
  );

  const otherTeachersThanMe = staffQuery.data!.teachers.filter((teacher) => teacher.id !== me?.id);

  return (
    <RosterManagementLayout>
      <GlobalStyle />
      {successErrorModal}
      <div className={'p-6 gap-20 flex'}>
        <Staff
          staff={otherTeachersThanMe}
          onSuccess={onSuccess}
          onError={onError}
          loading={staffQuery.isFetching}
        />
        <div className={'w-1 self-stretch bg-gray-400 opacity-25'}></div>
        <Classes
          classes={classesQuery.data!}
          onSuccess={onSuccess}
          onError={onError}
          loading={classesQuery.isFetching}
        />
      </div>
    </RosterManagementLayout>
  );
};

const GlobalStyle = createGlobalStyle`
  div[data-id=whizz_table] {
    height: 40vh;
    overflow-y: scroll;
  }
`;

const Staff = ({ staff, loading, onSuccess, onError }: { staff: StaffTeacher[], loading: boolean } & Pick<DeleteModalProps, 'onSuccess' | 'onError'>): JSX.Element => {
  const { translate } = useI18n();
  const [ selectedStaff, setSelectedStaff ] = useState<number[]>([]);
  const [ deleteModal, toggleDeleteModal, setDeleteModalProps ] = useModal((props: DeleteModalProps) => <DeleteStaffModal {...props} />);

  const onSubmit = () => {
    setDeleteModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.TIDY.STAFF.DELETE_MODAL.TITLE'),
      ids: selectedStaff,
      disableMaskClick: true,
      color: 'error',
      onSuccess: () => {
        setSelectedStaff([]);
        onSuccess?.();
      },
      onError,
    });

    toggleDeleteModal(true);
  };

  const onChange = (id: number) => {
    if (selectedStaff.includes(id)) {
      setSelectedStaff((prev) => prev.filter((i) => i !== id));
    } else {
      setSelectedStaff((prev) => [ ...prev, id ]);
    }
  };

  return (
    <>
      {deleteModal}
      <div className={'space-y-4 w-full'}>
        <h2 className={'font-bold text-lg'}>{translate('pages.ROSTER_MANAGEMENT.TIDY.STAFF.TITLE')}</h2>
        <p>{translate('pages.ROSTER_MANAGEMENT.TIDY.STAFF.CONTENT')}</p>
        <Table
          id={'tidy-staff'}
          showHeader={false}
          content={staff}
          noDataMessage={<NoData noDataMessage={translate(`pages.ROSTER_MANAGEMENT.TIDY.STAFF.${loading ? 'LOADING' : 'NO_DATA'}`)}/>}
          cols={[
            {
              Header: 'Name',
              Cell: ({ row }) => (
                <ChoiceGroup id={row.original.id} onChange={() => onChange(row.original.id)} checked={selectedStaff.includes(row.original.id)} label={`${row.original.firstName} ${row.original.lastName}`} />
              ),
            },
          ]}
        />
        <div className={'w-full text-center'}>
          <Button
            isOutline
            color={'error'}
            onClick={onSubmit}
            disabled={selectedStaff.length === 0}
          >{selectedStaff.length ? translate('pages.ROSTER_MANAGEMENT.TIDY.STAFF.DELETE', { count: selectedStaff.length }) : translate('pages.ROSTER_MANAGEMENT.TIDY.DELETE')}</Button>
        </div>
      </div>
    </>
  );
};

const Classes = ({ classes, loading, onSuccess, onError }: { classes: NewClassGet, loading: boolean } & Pick<DeleteModalProps, 'onSuccess' | 'onError'>): JSX.Element => {
  const { translate } = useI18n();
  const [ selectedClasses, setSelectedClasses ] = useState<number[]>([]);
  const [ deleteModal, toggleDeleteModal, setDeleteModalProps ] = useModal((props: DeleteModalProps) => <DeleteClassesModal {...props} />);

  const onSubmit = () => {
    setDeleteModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.TIDY.CLASSES.DELETE_MODAL.TITLE'),
      ids: selectedClasses,
      disableMaskClick: true,
      color: 'error',
      onSuccess: () => {
        setSelectedClasses([]);
        onSuccess?.();
      },
      onError,
    });

    toggleDeleteModal(true);
  };

  const onChange = (id: number) => {
    if (selectedClasses.includes(id)) {
      setSelectedClasses((prev) => prev.filter((i) => i !== id));
    } else {
      setSelectedClasses((prev) => [ ...prev, id ]);
    }
  };

  return (
    <>
      {deleteModal}
      <div className={'space-y-4 w-full'}>
        <h2 className={'font-bold text-lg'}>{translate('pages.ROSTER_MANAGEMENT.TIDY.CLASSES.TITLE')}</h2>
        <p>{translate('pages.ROSTER_MANAGEMENT.TIDY.CLASSES.CONTENT')}</p>
        <Table
          id={'tidy-classes'}
          showHeader={false}
          content={classes.filter((staffClass) => staffClass.studentCount === 0)}
          noDataMessage={<NoData noDataMessage={translate(`pages.ROSTER_MANAGEMENT.TIDY.CLASSES.${loading ? 'LOADING' : 'NO_DATA'}`)}/>}
          cols={[
            {
              Header: 'Name',
              Cell: ({ row }) => (
                <ChoiceGroup id={row.original.id} onChange={() => onChange(row.original.id)} checked={selectedClasses.includes(row.original.id)} label={`${row.original.name}`} />
              ),
            },
          ]}
        />
        <div className={'w-full text-center'}>
          <Button
            isOutline
            color={'error'}
            onClick={onSubmit}
            disabled={selectedClasses.length === 0}
          >{selectedClasses.length ? translate('pages.ROSTER_MANAGEMENT.TIDY.CLASSES.DELETE', { count: selectedClasses.length }) : translate('pages.ROSTER_MANAGEMENT.TIDY.DELETE')}</Button>
        </div>
      </div>
    </>
  );
};

interface DeleteModalProps extends Pick<ToggleModalProps, 'toggle'> {
  ids: number[];
  onSuccess?: () => void;
  onError?: () => void;
}

const DeleteStaffModal = ({ ids, toggle, onSuccess, onError }: DeleteModalProps): JSX.Element => {
  const { translate } = useI18n();
  const queryClient = useQueryClient();

  const { mutate: deleteStaff, isLoading } = useMutation(
    (staffId: number) => repository.StaffRepository.deleteStaff(staffId),
  );

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    ids.forEach((staffId) => {
      deleteStaff(staffId, {
        onSuccess: () => {
          queryClient.invalidateQueries([ QUERIES_IDS.STAFF, 'tidyUp' ]);
          onSuccess?.();
          toggle(false);
        },
        onError: () => {
          onError?.();
        },
      });
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={'container space-y-6'}>
        <p>{translate('pages.ROSTER_MANAGEMENT.TIDY.STAFF.DELETE_MODAL.CONTENT.LINE_1', { count: ids.length })}</p>
        <p className={'font-bold text-error'}>{translate('pages.ROSTER_MANAGEMENT.TIDY.STAFF.DELETE_MODAL.CONTENT.LINE_2')}</p>
      </div>
      <ModalFooter color={'error'}>
        <CancelButtonModal
          align={'start'}
          onClick={() => toggle(false)}
          disabled={isLoading}
        >
          {translate('pages.ROSTER_MANAGEMENT.TIDY.STAFF.DELETE_MODAL.BUTTONS.DISCARD')}
        </CancelButtonModal>
        <Button
          type={'submit'}
          icon={GLOBAL_ICONS.delete}
          color={'negative'}
          isOutline
          disabled={isLoading}
        >
          {!isLoading
            ? translate('pages.ROSTER_MANAGEMENT.TIDY.STAFF.DELETE_MODAL.BUTTONS.CONFIRM')
            : (<Loader disableLabel smallSize noMargin />)
          }
        </Button>
      </ModalFooter>
    </form>
  );
};

const DeleteClassesModal = ({ ids, toggle, onSuccess, onError }: DeleteModalProps): JSX.Element => {
  const { translate } = useI18n();
  const queryClient = useQueryClient();

  const { mutate: deleteClass, isLoading } = useMutation(
    (classId: number) => repository.NewClassRepository.delete(classId),
  );

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    ids.forEach((classId) => {
      deleteClass(classId, {
        onSuccess: () => {
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES, 'tidyUp' ]);
          queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ]);
          onSuccess?.();
          toggle(false);
        },
        onError: () => {
          onError?.();
        },
      });
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={'container space-y-6'}>
        <p>{translate('pages.ROSTER_MANAGEMENT.TIDY.CLASSES.DELETE_MODAL.CONTENT.LINE_1', { count: ids.length })}</p>
        <p className={'font-bold text-error'}>{translate('pages.ROSTER_MANAGEMENT.TIDY.CLASSES.DELETE_MODAL.CONTENT.LINE_2')}</p>
      </div>
      <ModalFooter color={'error'}>
        <CancelButtonModal
          align={'start'}
          onClick={() => toggle(false)}
          disabled={isLoading}
        >
          {translate('pages.ROSTER_MANAGEMENT.TIDY.CLASSES.DELETE_MODAL.BUTTONS.DISCARD')}
        </CancelButtonModal>
        <Button
          type={'submit'}
          icon={GLOBAL_ICONS.delete}
          color={'negative'}
          isOutline
          disabled={isLoading}
        >
          {!isLoading
            ? translate('pages.ROSTER_MANAGEMENT.TIDY.CLASSES.DELETE_MODAL.BUTTONS.CONFIRM')
            : (<Loader disableLabel smallSize noMargin />)
          }
        </Button>
      </ModalFooter>
    </form>
  );
};

export default Tidy;
